import {
  callNotification,
  errorCode,
  sessionExpired,
  workspaceNotExist,
  initialPanes,
} from "../constants";

/**
 * Generic error handler for API responses
 * @param {Object} response - The API response object
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Object} options - Additional options for handling specific error cases
 * @returns {Object} - Object containing error details and whether error was handled
 */
export const handleApiError = (response, dispatch = null, options = {}) => {
  const {
    suppressNotification = false,
    customErrorHandlers = {},
    showErrorCode21Notification = true,
  } = options;

  if (response?.data?.status === "error") {
    const errorCodeValue = response?.data?.error_code;
    let errorMessage = null;

    // Find the error message from errorCode array
    const errorInfo = errorCode?.find((el) => el.key === errorCodeValue);
    errorMessage = errorInfo?.value || "Unknown error occurred";

    // Check if there's a custom handler for this specific error code
    if (
      customErrorHandlers[errorCodeValue] &&
      typeof customErrorHandlers[errorCodeValue] === "function"
    ) {
      try {
        // Call custom handler with consistent parameters
        customErrorHandlers[errorCodeValue](
          errorCodeValue,
          errorMessage,
          response,
          dispatch
        );
      } catch (handlerError) {
        console.error("Error in custom error handler:", handlerError);
        // Fallback to default notification if custom handler fails
        if (!suppressNotification) {
          callNotification(errorMessage, "error");
        }
      }
      // Custom handlers are responsible for their own notifications
    } else if (
      customErrorHandlers.default &&
      typeof customErrorHandlers.default === "function"
    ) {
      try {
        // Use default custom handler if no specific handler found
        customErrorHandlers.default(
          errorCodeValue,
          errorMessage,
          response,
          dispatch
        );
      } catch (handlerError) {
        console.error("Error in default custom error handler:", handlerError);
        // Fallback to default notification if custom handler fails
        if (!suppressNotification) {
          callNotification(errorMessage, "error");
        }
      }
      // Custom default handler is responsible for its own notifications
    } else {
      // Handle specific error codes with default special logic only if no custom handler
      switch (errorCodeValue) {
        case 9: // Session expired
          if (dispatch) {
            sessionExpired(true);
          }
          if (!suppressNotification) {
            callNotification(errorMessage, "error");
          }
          break;

        case 11: // Workspace doesn't exist
          if (dispatch) {
            dispatch({ type: "SET_PANE", panes: initialPanes });
            workspaceNotExist(true);
          }
          if (!suppressNotification) {
            callNotification(errorMessage, "error");
          }
          break;

        case 21: // Restricted keywords detected
          // Show notification for error code 21 unless explicitly suppressed
          if (showErrorCode21Notification && !suppressNotification) {
            callNotification(errorMessage, "error");
          }
          break;

        default:
          // Show notification for all other errors unless suppressed
          if (!suppressNotification) {
            callNotification(errorMessage, "error");
          }
          break;
      }
    }

    return {
      hasError: true,
      errorCode: errorCodeValue,
      errorMessage,
      reason: response?.data?.reason,
    };
  }

  return {
    hasError: false,
    errorCode: null,
    errorMessage: null,
    reason: null,
  };
};

/**
 * Enhanced axios error handler that includes network errors
 * @param {Object} error - Axios error object
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Object} options - Additional options
 */
export const handleAxiosError = (error, dispatch = null, options = {}) => {
  const { suppressNotification = false } = options;

  // Handle network errors, timeouts, etc.
  if (error.response) {
    // Server responded with error status
    return handleApiError(error.response, dispatch, options);
  } else if (error.request) {
    // Network error
    if (!suppressNotification && error.message !== "Request aborted") {
      callNotification("Network error. Please check your connection.", "error");
    }
  } else {
    // Other errors
    if (!suppressNotification && error.message !== "Request aborted") {
      callNotification("An unexpected error occurred.", "error");
    }
  }

  return {
    hasError: true,
    errorCode: null,
    errorMessage: "Network or unexpected error",
    reason: error.message,
  };
};

/**
 * Wrapper for axios requests with built-in error handling
 * @param {Function} axiosCall - The axios call function
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} options - Options for error handling
 * @returns {Promise} - Promise that resolves with response or error details
 */
export const makeApiCallWithErrorHandling = async (
  axiosCall,
  dispatch = null,
  options = {}
) => {
  try {
    const response = await axiosCall();
    const errorResult = handleApiError(response, dispatch, options);

    return {
      response,
      ...errorResult,
    };
  } catch (error) {
    const errorResult = handleAxiosError(error, dispatch, options);

    return {
      response: null,
      ...errorResult,
    };
  }
};
